import { useState, useEffect, useRef, useCallback } from "react";

// ─── STYLES ───────────────────────────────────────────────────────────────────
const GOOGLE_FONTS = `@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');`;

const CSS = `
  *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
  html { scroll-behavior:smooth; }
  body { font-family:'DM Sans',sans-serif; background:#0a0e1a; color:#f1f5f9; }
  :root {
    --bg:#0a0e1a; --card:#111827; --card2:#1a2235;
    --accent:#f97316; --accent2:#fb923c;
    --blue:#38bdf8; --green:#4ade80; --purple:#a78bfa;
    --text:#f1f5f9; --muted:#94a3b8; --border:#1e293b; --radius:16px;
  }
  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin   { to{transform:rotate(360deg)} }
  .fade-up { animation: fadeUp .3s ease; }
  .spinner {
    width:20px; height:20px; border-radius:50%;
    border:2px solid var(--border); border-top-color:var(--accent);
    animation: spin .7s linear infinite; flex-shrink:0;
  }
  .leaflet-container { border-radius: var(--radius); }
  input, select {
    background:var(--card2); border:1px solid var(--border); border-radius:10px;
    padding:10px 14px; color:var(--text); font-family:'DM Sans',sans-serif;
    font-size:0.9rem; outline:none; width:100%; transition:border-color .2s;
  }
  input:focus, select:focus { border-color:var(--accent); }
  input::placeholder { color:var(--muted); }
  select option { background:var(--card); }
  ::-webkit-scrollbar { display:none; }
`;

// ─── BACKEND URL ──────────────────────────────────────────────────────────────
const API_BASE = "https://api.tripplanner.msdiscoveries.com";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt         = (n) => Number(n).toLocaleString("en-IN");
const makeStars   = (r) => "★".repeat(Math.round(r)) + "☆".repeat(5 - Math.round(r));
const ratingLabel = (r) => r >= 4.7 ? "Superb" : r >= 4.4 ? "Excellent" : r >= 4.0 ? "Very Good" : "Good";
const ratingColor = (r) => r >= 4.5 ? "#4ade80" : "#fbbf24";
const today          = new Date().toISOString().split("T")[0];
const threeDaysLater = new Date(Date.now() + 3 * 86400000).toISOString().split("T")[0];

// ─── AI CALL — points to backend ─────────────────────────────────────────────
async function callAI(prompt) {
  const res = await fetch(`${API_BASE}/api/ai`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 2000,
      system: "You are a travel data API. You MUST respond with ONLY a valid JSON object. No markdown, no backticks, no explanation, no extra text before or after the JSON. Start your response with { and end with }.",
      messages: [{ role: "user", content: prompt }],
    }),
  });
  if (!res.ok) throw new Error("API error: " + res.status);
  const data = await res.json();
  if (data.error) throw new Error(data.error.message || "API error");
  const raw  = (data.content || []).map((b) => b.text || "").join("").trim();
  // Strip markdown fences if present
  const clean = raw.replace(/^```json\s*/i, "").replace(/^```\s*/i, "").replace(/\s*```$/i, "").trim();
  // Extract JSON object if there's surrounding text
  const match = clean.match(/\{[\s\S]*\}/);
  if (!match) throw new Error("No JSON found in response: " + clean.slice(0, 100));
  return JSON.parse(match[0]);
}

// ─── FALLBACKS ────────────────────────────────────────────────────────────────
const fallbackTransport = () => [
  { type:"Flight", provider:"IndiGo",        price:3500, duration:"1h 50m", departure:"07:00", arrival:"08:50",   comfort:"High",   features:["Snack","Baggage 15kg"] },
  { type:"Train",  provider:"Express Train",  price:950,  duration:"16h",    departure:"17:00", arrival:"09:00+1", comfort:"Medium", features:["AC 2T","Pantry Car"]    },
  { type:"Bus",    provider:"RedBus Sleeper", price:480,  duration:"14h",    departure:"21:00", arrival:"11:00+1", comfort:"Low",    features:["Sleeper","AC"]          },
];
const fallbackHotels = () => [
  { name:"Grand City Hotel",      area:"City Centre",   stars:5, rating:4.8, reviews:2100, price_per_night:8000, amenities:["Pool","Gym","Spa"],               tip:"Ask for upper floor"   },
  { name:"Comfort Inn Express",   area:"Near Railway",  stars:3, rating:4.2, reviews:850,  price_per_night:2200, amenities:["Breakfast","WiFi"],               tip:"Good for short stays"  },
  { name:"Budget Stay Plus",      area:"Old Town",      stars:2, rating:3.9, reviews:420,  price_per_night:800,  amenities:["WiFi","AC"],                      tip:"Budget friendly"       },
  { name:"Heritage Palace Hotel", area:"Heritage Zone", stars:4, rating:4.6, reviews:1580, price_per_night:4500, amenities:["Heritage","Restaurant","Garden"], tip:"Book heritage suite"   },
];
const fallbackRestaurants = () => [
  { name:"The Local Thali House", area:"Old City",      cuisine:"Regional",    rating:4.6, reviews:1200, avg_meal_per_person:250, specialty:"Unlimited Thali",    open_hours:"11am–10pm", tags:["Local Fav","Value"]     },
  { name:"Spice Garden",          area:"City Centre",   cuisine:"North Indian",rating:4.4, reviews:980,  avg_meal_per_person:450, specialty:"Butter Chicken",    open_hours:"12pm–11pm", tags:["Family","Cozy"]         },
  { name:"Sea Breeze Seafood",    area:"Waterfront",    cuisine:"Seafood",     rating:4.8, reviews:2100, avg_meal_per_person:700, specialty:"Grilled Pomfret",   open_hours:"1pm–11pm",  tags:["Top Rated","Scenic"]    },
  { name:"Street Bites Corner",   area:"Market Street", cuisine:"Street Food", rating:4.3, reviews:650,  avg_meal_per_person:120, specialty:"Vada Pav",          open_hours:"8am–10pm",  tags:["Budget","Authentic"]    },
  { name:"The Heritage Dining",   area:"Heritage Zone", cuisine:"Continental", rating:4.5, reviews:870,  avg_meal_per_person:900, specialty:"Grilled Platter",   open_hours:"7pm–11pm",  tags:["Fine Dining","Elegant"] },
  { name:"Chai & Chaat Café",     area:"Near Station",  cuisine:"Café/Snacks", rating:4.2, reviews:540,  avg_meal_per_person:180, specialty:"Masala Chai & Bhel",open_hours:"7am–9pm",  tags:["Quick Bite","Morning"]  },
];
const fallbackPlaces = () => [
  { name:"Heritage Fort",     area:"Old City",      entry_fee:50,  duration_hrs:2,   category:"Monument", rating:4.6, reviews:8500, tip:"Visit at sunrise"        },
  { name:"Botanical Garden",  area:"East Side",     entry_fee:20,  duration_hrs:2,   category:"Nature",   rating:4.4, reviews:3200, tip:"Great for morning walks" },
  { name:"Local Bazaar",      area:"Market Area",   entry_fee:0,   duration_hrs:1.5, category:"Market",   rating:4.3, reviews:2100, tip:"Bargain for best prices" },
  { name:"Main Temple",       area:"Temple Street", entry_fee:0,   duration_hrs:1,   category:"Temple",   rating:4.7, reviews:6800, tip:"Dress modestly"          },
  { name:"Viewpoint Hill",    area:"Hilltop",       entry_fee:30,  duration_hrs:1.5, category:"Nature",   rating:4.5, reviews:4200, tip:"Perfect for sunset"      },
  { name:"Museum of History", area:"City Centre",   entry_fee:100, duration_hrs:2.5, category:"Museum",   rating:4.4, reviews:3100, tip:"Audio guide available"   },
];

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
function Spinner({ text = "Loading..." }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, color:"var(--muted)", padding:"1.5rem", justifyContent:"center" }}>
      <div className="spinner" />{text}
    </div>
  );
}

function Card({ title, icon, children }) {
  return (
    <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:"var(--radius)", padding:"1.5rem", marginBottom:"1.2rem" }}>
      {title && (
        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"1rem", marginBottom:"1rem", display:"flex", alignItems:"center", gap:8 }}>
          <span>{icon}</span>{title}
        </div>
      )}
      {children}
    </div>
  );
}

function Btn({ variant = "primary", onClick, disabled, children }) {
  const base = {
    display:"inline-flex", alignItems:"center", gap:8,
    padding:"12px 24px", borderRadius:12,
    fontFamily:"'DM Sans',sans-serif", fontSize:"0.9rem", fontWeight:600,
    cursor: disabled ? "not-allowed" : "pointer",
    transition:"all .2s", border:"none", opacity: disabled ? 0.5 : 1,
  };
  const styles = {
    primary:   { background:"linear-gradient(135deg,#f97316,#fb923c)", color:"#fff" },
    secondary: { background:"var(--card2)", color:"var(--text)", border:"1px solid var(--border)" },
  };
  return (
    <button style={{ ...base, ...styles[variant] }} onClick={disabled ? undefined : onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function Tag({ children, color }) {
  const map = {
    default: { background:"var(--border)",              color:"var(--muted)" },
    green:   { background:"rgba(74,222,128,0.12)",      color:"#4ade80"      },
    blue:    { background:"rgba(56,189,248,0.12)",      color:"#38bdf8"      },
    orange:  { background:"rgba(249,115,22,0.12)",      color:"#f97316"      },
    purple:  { background:"rgba(167,139,250,0.12)",     color:"#a78bfa"      },
  };
  return (
    <span style={{ ...map[color || "default"], padding:"3px 10px", borderRadius:20, fontSize:".72rem" }}>
      {children}
    </span>
  );
}

function OptionCard({ selected, onClick, children }) {
  return (
    <div onClick={onClick} style={{
      background: selected ? "rgba(249,115,22,0.08)" : "var(--card2)",
      border: `2px solid ${selected ? "#f97316" : "var(--border)"}`,
      borderRadius:"var(--radius)", padding:"1.2rem",
      cursor:"pointer", transition:"all .2s", position:"relative", overflow:"hidden",
    }}>
      {selected && (
        <div style={{
          position:"absolute", top:12, right:12, width:22, height:22,
          borderRadius:"50%", background:"#f97316", color:"#fff",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:".7rem", fontWeight:700,
        }}>✓</div>
      )}
      {children}
    </div>
  );
}

function RatingRow({ rating, reviews }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, margin:"6px 0", flexWrap:"wrap" }}>
      <span style={{ color:"#fbbf24", fontSize:".85rem", letterSpacing:1 }}>{makeStars(rating)}</span>
      <span style={{ background:"rgba(74,222,128,0.12)", color:ratingColor(rating), borderRadius:6, padding:"2px 8px", fontSize:".72rem", fontWeight:600 }}>
        {rating} · {ratingLabel(rating)}
      </span>
      <span style={{ fontSize:".7rem", color:"var(--muted)" }}>({fmt(reviews)})</span>
    </div>
  );
}

function BtnRow({ children }) {
  return <div style={{ display:"flex", gap:"1rem", flexWrap:"wrap", marginTop:"1.2rem" }}>{children}</div>;
}

// ─── STEP 1: Trip Details ──────────────────────────────────────────────────────
function StepDetails({ onSearch }) {
  const [form, setForm] = useState({
    from:"", to:"", date:today, returnDate:threeDaysLater, travellers:2, budgetType:"mid",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const lbl = { fontSize:".78rem", fontWeight:500, color:"var(--muted)", textTransform:"uppercase", letterSpacing:".5px", display:"block", marginBottom:6 };

  return (
    <div className="fade-up">
      <Card title="Where are you going?" icon="✈️">
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:"1rem" }}>
          <div><label style={lbl}>From City</label><input placeholder="e.g. Chennai" value={form.from} onChange={e=>set("from",e.target.value)} /></div>
          <div><label style={lbl}>To City</label><input placeholder="e.g. Mumbai" value={form.to} onChange={e=>set("to",e.target.value)} /></div>
          <div><label style={lbl}>Travel Date</label><input type="date" value={form.date} onChange={e=>set("date",e.target.value)} /></div>
          <div><label style={lbl}>Return Date</label><input type="date" value={form.returnDate} onChange={e=>set("returnDate",e.target.value)} /></div>
          <div><label style={lbl}>Travellers</label><input type="number" min="1" max="20" value={form.travellers} onChange={e=>set("travellers",parseInt(e.target.value)||1)} /></div>
          <div>
            <label style={lbl}>Budget Type</label>
            <select value={form.budgetType} onChange={e=>set("budgetType",e.target.value)}>
              <option value="budget">💰 Budget (Economy)</option>
              <option value="mid">🌟 Mid-range</option>
              <option value="luxury">💎 Luxury</option>
            </select>
          </div>
        </div>
      </Card>
      <div style={{ background:"linear-gradient(135deg,rgba(249,115,22,0.1),rgba(56,189,248,0.05))", border:"1px solid rgba(249,115,22,0.3)", borderRadius:"var(--radius)", padding:"1.5rem", textAlign:"center" }}>
        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"1rem", marginBottom:6 }}>🤖 AI-Powered Trip Intelligence</div>
        <div style={{ color:"var(--muted)", fontSize:".85rem", marginBottom:"1rem" }}>Our AI searches real prices for transport, hotels, restaurants & places</div>
        <Btn onClick={() => { if (!form.from || !form.to) { alert("Please enter both cities!"); return; } onSearch(form); }}>
          🔍 Search & Plan My Trip
        </Btn>
      </div>
    </div>
  );
}

// ─── STEP 2: Transport ────────────────────────────────────────────────────────
function StepTransport({ data, loading, selected, onSelect, onNext, onBack }) {
  const typeIcon  = { Flight:"✈️", Train:"🚂", Bus:"🚌" };
  const typeColor = { Flight:"blue", Train:"green", Bus:"orange" };
  return (
    <div className="fade-up">
      <Card title="Choose Your Transport" icon="🚀">
        {loading ? <Spinner text="Searching flights, trains & buses..." /> : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:"1rem" }}>
            {data.map((o, i) => (
              <OptionCard key={i} selected={selected?.provider === o.provider} onClick={() => onSelect(o)}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:".6rem" }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:".95rem" }}>{typeIcon[o.type]||"🚗"} {o.provider}</div>
                  <Tag color={typeColor[o.type]}>{o.type}</Tag>
                </div>
                <div style={{ color:"#f97316", fontWeight:700, fontSize:"1.05rem" }}>
                  {o.currency_symbol||"₹"}{fmt(o.price)}
                  {o.currency_symbol==="$" && (
                    <span style={{fontSize:".72rem",color:"var(--muted)",fontWeight:400,marginLeft:4}}>
                      (≈₹{fmt(o.inr_equivalent||o.price*92)})
                    </span>
                  )}
                </div>
                <div style={{ color:"var(--muted)", fontSize:".8rem", margin:"4px 0" }}>{o.duration} · {o.departure} → {o.arrival}</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>{o.features.map(f => <Tag key={f}>{f}</Tag>)}</div>
              </OptionCard>
            ))}
          </div>
        )}
      </Card>
      <BtnRow>
        <Btn variant="secondary" onClick={onBack}>← Back</Btn>
        <Btn onClick={onNext} disabled={!selected}>Next: Route Map →</Btn>
      </BtnRow>
    </div>
  );
}

// ─── STEP 3: Map ──────────────────────────────────────────────────────────────
function StepMap({ from, to, onNext, onBack }) {
  const mapRef      = useRef(null);
  const instanceRef = useRef(null);
  const [info, setInfo] = useState("");

  useEffect(() => {
    const tryInit = () => {
      if (!window.L) { setTimeout(tryInit, 300); return; }
      if (instanceRef.current) { instanceRef.current.remove(); instanceRef.current = null; }
      const map = window.L.map(mapRef.current, { zoomControl:true });
      instanceRef.current = map;
      window.L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
        attribution:"© OpenStreetMap © Carto", maxZoom:18,
      }).addTo(map);
      // Search without hardcoded country so international cities work correctly
      Promise.all([
        fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(from)}`).then(r=>r.json()),
        fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(to)}`).then(r=>r.json()),
      ]).then(([r1, r2]) => {
        if (!r1[0] || !r2[0]) return;
        const p1 = [parseFloat(r1[0].lat), parseFloat(r1[0].lon)];
        const p2 = [parseFloat(r2[0].lat), parseFloat(r2[0].lon)];
        const dotIcon = window.L.divIcon({
          className:"",
          html:`<div style="width:14px;height:14px;border-radius:50%;background:#f97316;border:2px solid #fff;box-shadow:0 0 8px rgba(249,115,22,.6)"></div>`,
          iconSize:[14,14], iconAnchor:[7,7],
        });
        window.L.marker(p1,{icon:dotIcon}).addTo(map).bindPopup(`<b>🛫 ${from}</b>`);
        window.L.marker(p2,{icon:dotIcon}).addTo(map).bindPopup(`<b>🛬 ${to}</b>`);
        window.L.polyline([p1,p2],{color:"#f97316",weight:2.5,dashArray:"8,6",opacity:.8}).addTo(map);
        map.fitBounds([p1,p2],{padding:[40,40]});
        const dist = Math.round(map.distance(p1,p2)/1000);
        setInfo(`📍 ${from} → ${to} · ~${dist} km straight line`);
      }).catch(() => {});
    };
    tryInit();
    return () => { if (instanceRef.current) { instanceRef.current.remove(); instanceRef.current = null; } };
  }, [from, to]);

  return (
    <div className="fade-up">
      <Card title="Route Overview" icon="🗺️">
        <div ref={mapRef} style={{ height:320, borderRadius:"var(--radius)", overflow:"hidden", border:"1px solid var(--border)", marginBottom:"1rem" }} />
        <div style={{ color:"var(--muted)", fontSize:".85rem", textAlign:"center" }}>{info}</div>
      </Card>
      <BtnRow>
        <Btn variant="secondary" onClick={onBack}>← Back</Btn>
        <Btn onClick={onNext}>Next: Hotels →</Btn>
      </BtnRow>
    </div>
  );
}

// ─── STEP 4: Hotels ───────────────────────────────────────────────────────────
function StepHotels({ data, loading, selected, onSelect, onNext, onBack }) {
  const sorted = [...data].sort((a,b) => b.rating - a.rating);
  return (
    <div className="fade-up">
      <Card title="Pick Your Stay" icon="🏨">
        {loading ? <Spinner text="Finding best-rated hotels..." /> : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:"1rem" }}>
            {sorted.map((h, i) => (
              <OptionCard key={i} selected={selected?.name === h.name} onClick={() => onSelect(h)}>
                {i === 0 && (
                  <div style={{ marginBottom:6 }}>
                    <span style={{ background:"linear-gradient(135deg,#fbbf24,#f97316)", color:"#000", fontSize:".65rem", fontWeight:700, padding:"2px 8px", borderRadius:20, textTransform:"uppercase" }}>
                      🏆 Top Rated
                    </span>
                  </div>
                )}
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:".95rem" }}>{h.name}</div>
                <div style={{ color:"var(--muted)", fontSize:".78rem", marginBottom:4 }}>📍 {h.area}</div>
                <RatingRow rating={h.rating} reviews={h.reviews} />
                <div style={{ color:"#f97316", fontWeight:700, fontSize:"1.05rem" }}>
                  {h.currency_symbol||"₹"}{fmt(h.price_per_night)}
                  <span style={{ fontSize:".75rem", color:"var(--muted)", fontWeight:400 }}> /night</span>
                  {h.currency_symbol==="$" && (
                    <span style={{fontSize:".72rem",color:"var(--muted)",display:"block"}}>
                      (≈₹{fmt(h.inr_equivalent||h.price_per_night*92)}/night)
                    </span>
                  )}
                </div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>{h.amenities.map(a => <Tag key={a}>{a}</Tag>)}</div>
                {h.tip && <div style={{ fontSize:".75rem", color:"#38bdf8", marginTop:6 }}>💡 {h.tip}</div>}
              </OptionCard>
            ))}
          </div>
        )}
      </Card>
      <BtnRow>
        <Btn variant="secondary" onClick={onBack}>← Back</Btn>
        <Btn onClick={onNext} disabled={!selected}>Next: Restaurants →</Btn>
      </BtnRow>
    </div>
  );
}

// ─── STEP 5: Restaurants ──────────────────────────────────────────────────────
function StepRestaurants({ data, loading, selected, onToggle, onNext, onBack }) {
  return (
    <div className="fade-up">
      <Card title="Top Restaurants to Try" icon="🍽️">
        <p style={{ color:"var(--muted)", fontSize:".85rem", marginBottom:"1rem" }}>Select restaurants you'd like to visit — costs added to budget</p>
        {loading ? <Spinner text="Finding top-rated restaurants..." /> : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:"1rem" }}>
            {data.map((r, i) => {
              const sel = selected.some(x => x.name === r.name);
              return (
                <OptionCard key={i} selected={sel} onClick={() => onToggle(r)}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:".95rem" }}>{r.name}</div>
                    <Tag color="purple">{r.cuisine}</Tag>
                  </div>
                  <div style={{ color:"var(--muted)", fontSize:".75rem" }}>📍 {r.area} · ⏰ {r.open_hours}</div>
                  <div style={{ color:"#38bdf8", fontSize:".78rem", fontStyle:"italic", marginTop:2 }}>⭐ {r.specialty}</div>
                  <RatingRow rating={r.rating} reviews={r.reviews} />
                  <div style={{ color:"#f97316", fontWeight:700, fontSize:"1.05rem" }}>
                    {r.currency_symbol||"₹"}{fmt(r.avg_meal_per_person)}
                    <span style={{ fontSize:".75rem", color:"var(--muted)", fontWeight:400 }}> /person/meal</span>
                    {r.currency_symbol==="$" && (
                      <span style={{fontSize:".72rem",color:"var(--muted)",display:"block"}}>
                        (≈₹{fmt(r.inr_equivalent||r.avg_meal_per_person*92)}/meal)
                      </span>
                    )}
                  </div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>{(r.tags||[]).map(t => <Tag key={t}>{t}</Tag>)}</div>
                </OptionCard>
              );
            })}
          </div>
        )}
      </Card>
      <BtnRow>
        <Btn variant="secondary" onClick={onBack}>← Back</Btn>
        <Btn onClick={onNext}>Next: Places →</Btn>
      </BtnRow>
    </div>
  );
}

// ─── STEP 6: Places ───────────────────────────────────────────────────────────
function StepPlaces({ data, loading, selected, onToggle, onNext, onBack }) {
  const catIcons = { Monument:"🏛️", Temple:"🛕", Nature:"🌿", Market:"🛍️", Museum:"🏛️", Beach:"🏖️", Park:"🌳" };
  return (
    <div className="fade-up">
      <Card title="Places to Visit" icon="📍">
        <p style={{ color:"var(--muted)", fontSize:".85rem", marginBottom:"1rem" }}>Select attractions — entry fees auto-added to budget</p>
        {loading ? <Spinner text="Discovering attractions..." /> : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:"1rem" }}>
            {data.map((p, i) => {
              const sel = selected.some(x => x.name === p.name);
              return (
                <OptionCard key={i} selected={sel} onClick={() => onToggle(p)}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:".95rem" }}>{catIcons[p.category]||"📍"} {p.name}</div>
                    <Tag>{p.category}</Tag>
                  </div>
                  <div style={{ color:"var(--muted)", fontSize:".75rem" }}>📍 {p.area}</div>
                  <RatingRow rating={p.rating} reviews={p.reviews} />
                  <div style={{ color: p.entry_fee === 0 ? "#4ade80" : "#f97316", fontWeight:700, fontSize:"1rem" }}>
                    {p.entry_fee === 0 ? "Free Entry" : `${p.currency_symbol||"₹"}${fmt(p.entry_fee)} entry`}
                    {p.currency_symbol==="$" && p.entry_fee > 0 && (
                      <span style={{fontSize:".72rem",color:"var(--muted)",marginLeft:4}}>
                        (≈₹{fmt(p.inr_equivalent||p.entry_fee*92)})
                      </span>
                    )}
                  </div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>
                    <Tag>⏱ {p.duration_hrs}h visit</Tag>
                    {p.tip && <Tag color="blue">💡 {p.tip}</Tag>}
                  </div>
                </OptionCard>
              );
            })}
          </div>
        )}
      </Card>
      <BtnRow>
        <Btn variant="secondary" onClick={onBack}>← Back</Btn>
        <Btn onClick={onNext}>Next: Itinerary →</Btn>
      </BtnRow>
    </div>
  );
}

// ─── STEP 7: Itinerary ────────────────────────────────────────────────────────
function StepItinerary({ tripState, onNext, onBack }) {
  const { from, to, days, travellers, budgetType, hotel, transport, restaurants, places, restaurantData, placesData } = tripState;
  const activePlaces = places.length ? places : placesData.slice(0, 4);
  const activeRests  = restaurants.length ? restaurants : restaurantData.slice(0, 3);

  // Parse duration string like "16h", "1h 45m", "38h" into total hours
  const parseDurationHours = (dur) => {
    if (!dur) return 2;
    const h = parseInt(dur.match(/(\d+)h/)?.[1] || 0);
    const m = parseInt(dur.match(/(\d+)m/)?.[1] || 0);
    return h + m / 60;
  };

  const travelHours = transport ? parseDurationHours(transport.duration) : 0;
  const travelIcon  = { Flight:"✈️", Train:"🚂", Bus:"🚌" };

  // Day 1 = travel day if journey is under 12 hours (flight/short train)
  // Long journeys (train >12h, bus >12h) = depart prev night, arrive day 1 morning
  const isOvernightTravel = travelHours > 12;

  const buildDay = (d) => {
    const dayPlaces  = activePlaces.filter((_, i) => i % days === (d - 1) % days);
    const lunchRest  = activeRests[(d - 1) % activeRests.length];
    const dinnerRest = activeRests[d % activeRests.length] || activeRests[0];
    const slots = [];

    if (d === 1 && transport) {
      if (isOvernightTravel) {
        // Long journey — depart night before, arrive morning of day 1
        slots.push({ time: transport.departure || "21:00", icon: travelIcon[transport.type]||"🚌", title:`Depart from ${from}`, sub:`${transport.type} · ${transport.provider} · ${transport.duration} journey` });
        slots.push({ time: transport.arrival   || "06:00", icon:"🏙️", title:`Arrive in ${to}`, sub:`Journey takes ${transport.duration}. Check-in at ${hotel?.name || "hotel"}, freshen up` });
        slots.push({ time:"09:00", icon:"☕", title:"Breakfast after arrival", sub:"Morning meal near hotel" });
      } else {
        // Short journey — depart morning, arrive same day
        slots.push({ time:"06:30", icon:"🌅", title:"Wake Up & Get Ready",   sub:`Pack up and head to ${transport.type === "Flight" ? "airport" : transport.type === "Train" ? "railway station" : "bus station"}` });
        slots.push({ time: transport.departure || "08:00", icon: travelIcon[transport.type]||"✈️", title:`Depart from ${from}`, sub:`${transport.type} · ${transport.provider} · Duration: ${transport.duration}` });
        slots.push({ time: transport.arrival   || "10:00", icon:"🏙️", title:`Arrive in ${to}`, sub:`Check-in at ${hotel?.name || "hotel"}, freshen up and rest` });
        slots.push({ time:"12:00", icon:"☕", title:"Freshen Up & Lunch",     sub:"Settle in, grab lunch near hotel" });
      }
    } else {
      slots.push({ time:"06:30", icon:"🌅", title:"Wake Up & Freshen Up",   sub:`Start your day at ${hotel?.name || "your hotel"}` });
      slots.push({ time:"07:30", icon:"☕", title:"Breakfast",               sub:"Morning meal at hotel or nearby café" });
    }

    if (dayPlaces[0]) slots.push({ time:"11:00", icon:"📍", title:`Visit ${dayPlaces[0].name}`, sub:`${dayPlaces[0].area} · ~${dayPlaces[0].duration_hrs}h visit${dayPlaces[0].tip ? " · 💡 " + dayPlaces[0].tip : ""}` });
    if (lunchRest)    slots.push({ time:"13:00", icon:"🍽️", title:`Lunch at ${lunchRest.name}`,  sub:`${lunchRest.cuisine} · ${lunchRest.area} · ~₹${lunchRest.avg_meal_per_person}/person` });
    if (dayPlaces[1]) slots.push({ time:"14:30", icon:"📍", title:`Visit ${dayPlaces[1].name}`, sub:`${dayPlaces[1].area} · ~${dayPlaces[1].duration_hrs}h visit${dayPlaces[1].tip ? " · 💡 " + dayPlaces[1].tip : ""}` });
    if (dayPlaces[2]) slots.push({ time:"16:30", icon:"📍", title:`Visit ${dayPlaces[2].name}`, sub:`${dayPlaces[2].area} · ~${dayPlaces[2].duration_hrs}h visit${dayPlaces[2].tip ? " · 💡 " + dayPlaces[2].tip : ""}` });
    slots.push({ time:"18:00", icon:"🛍️", title:"Evening Leisure",           sub:"Explore local market or take a stroll" });
    if (dinnerRest) slots.push({ time:"19:30", icon:"🌙", title:`Dinner at ${dinnerRest.name}`, sub:`${dinnerRest.cuisine} · ${dinnerRest.area} · ~₹${dinnerRest.avg_meal_per_person}/person` });
    slots.push({ time:"21:30", icon:"🏨", title:"Return to Hotel",           sub:"Rest & recharge for tomorrow" });
    return slots;
  };

  return (
    <div className="fade-up">
      <Card title="Your Day-by-Day Itinerary" icon="🗓️">
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:"1.5rem" }}>
          {[`${from} → ${to}`, `${days} days · ${travellers} traveller${travellers > 1 ? "s" : ""}`, `${budgetType} budget`].map((t, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:6, background:"var(--card2)", border:"1px solid var(--border)", borderRadius:20, padding:"5px 14px", fontSize:".8rem" }}>
              <div style={{ width:7, height:7, borderRadius:"50%", background:["#f97316","#38bdf8","#4ade80"][i] }} />{t}
            </div>
          ))}
        </div>
        {Array.from({ length: days }, (_, di) => (
          <div key={di} style={{ borderLeft:"2px solid #f97316", paddingLeft:"1.2rem", marginBottom:"1.5rem" }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:"#f97316", fontSize:".85rem", textTransform:"uppercase", letterSpacing:1, marginBottom:".6rem" }}>
              Day {di + 1} — {to}
            </div>
            {buildDay(di + 1).map((s, si) => (
              <div key={si} style={{ background:"var(--card2)", borderRadius:10, padding:".8rem 1rem", marginBottom:".6rem", display:"flex", gap:".8rem", alignItems:"flex-start" }}>
                <div style={{ color:"var(--muted)", fontSize:".75rem", minWidth:55, marginTop:2, fontWeight:600 }}>{s.time}</div>
                <div>
                  <div style={{ fontWeight:500, fontSize:".88rem" }}>{s.icon} {s.title}</div>
                  <div style={{ color:"var(--muted)", fontSize:".75rem" }}>{s.sub}</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </Card>
      <BtnRow>
        <Btn variant="secondary" onClick={onBack}>← Back</Btn>
        <Btn onClick={onNext}>Next: Budget →</Btn>
      </BtnRow>
    </div>
  );
}

// ─── STEP 8: Budget ───────────────────────────────────────────────────────────
function StepBudget({ tripState, onBack, onReset }) {
  const { from, to, travellers, days, transport, hotel, restaurants, places } = tripState;
  const n = travellers;
  const USD_TO_INR = 92; // approximate conversion rate

  // Convert all prices to INR for unified budget calculation
  const toINR = (item, priceKey) => {
    if (!item) return 0;
    const price = item[priceKey] || 0;
    if (item.currency_symbol === "$") return (item.inr_equivalent || price * USD_TO_INR);
    return price;
  };

  const isIntl = (transport?.currency_symbol === "$") || (hotel?.currency_symbol === "$");
  const displaySymbol = isIntl ? "₹" : "₹"; // always show INR in budget for consistency

  const transportCost = transport ? toINR(transport, "price") * n : 0;
  const hotelCost     = hotel     ? toINR(hotel, "price_per_night") * days : 0;
  const restCost      = restaurants.reduce((s, r) => s + toINR(r, "avg_meal_per_person") * n * days, 0);
  const placesCost    = places.reduce((s, p) => s + toINR(p, "entry_fee") * n, 0);
  const miscCost      = Math.round((transportCost + hotelCost + restCost + placesCost) * 0.1);
  const total         = transportCost + hotelCost + restCost + placesCost + miscCost;
  const perPerson     = Math.round(total / n);

  const items = [
    { label:"✈️ Transport",      cost:transportCost, color:"#38bdf8" },
    { label:"🏨 Hotel Stay",     cost:hotelCost,     color:"#a78bfa" },
    { label:"🍽️ Restaurants",   cost:restCost,      color:"#f97316" },
    { label:"📍 Places & Entry", cost:placesCost,    color:"#4ade80" },
    { label:"🎒 Misc / Tips",    cost:miscCost,      color:"#fbbf24" },
  ];

  const shareWA = () => {
    const text = `🌍 *Trip Planner*\n✈️ ${from} → ${to}\n📅 ${days} days · ${n} traveller${n>1?"s":""}\n🚗 ${transport ? transport.type+" ("+transport.provider+")" : "TBD"}\n🏨 ${hotel?.name||"TBD"}\n💰 Total Budget (INR): ₹${fmt(total)}\n💵 Per Person: ₹${fmt(perPerson)}\n\nPlanned with Trip Planner Pro 🚀\ntripplanner.msdiscoveries.com`;
    window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
  };
  const copyPlan = () => {
    const text = `Trip Planner Pro\n${from} → ${to} | ${days} days | ${n} travellers\nTransport: ${transport ? transport.provider+" ₹"+transport.price : "TBD"}\nHotel: ${hotel ? hotel.name+" ₹"+hotel.price_per_night+"/night" : "TBD"}\nRestaurants: ${restaurants.map(r=>r.name).join(", ")||"TBD"}\nPlaces: ${places.map(p=>p.name).join(", ")||"TBD"}\nTotal: ₹${fmt(total)}\n\ntripplanner.msdiscoveries.com`;
    navigator.clipboard.writeText(text).then(() => alert("Trip plan copied! ✓"));
  };

  return (
    <div className="fade-up">
      <Card title="Full Trip Budget" icon="💰">
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:"1.5rem" }}>
          <div>
            {items.map(it => (
              <div key={it.label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid var(--border)" }}>
                <span style={{ fontSize:".9rem" }}>{it.label}</span>
                <strong>₹{fmt(it.cost)}</strong>
              </div>
            ))}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 0 0", fontFamily:"'Syne',sans-serif", fontSize:"1.1rem", fontWeight:700, color:"#f97316" }}>
              <span>💰 Total</span><strong>₹{fmt(total)}</strong>
            </div>
          </div>
          <div>
            {items.filter(it => it.cost > 0).map(it => (
              <div key={it.label} style={{ marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:".78rem", color:"var(--muted)", marginBottom:4 }}>
                  <span>{it.label}</span><span>₹{fmt(it.cost)}</span>
                </div>
                <div style={{ height:10, borderRadius:20, background:"var(--border)", overflow:"hidden" }}>
                  <div style={{ height:"100%", borderRadius:20, background:it.color, width:`${Math.round(it.cost/total*100)}%`, transition:"width .8s ease" }} />
                </div>
              </div>
            ))}
            <div style={{ background:"linear-gradient(135deg,rgba(56,189,248,0.1),rgba(167,139,250,0.1))", border:"1px solid rgba(56,189,248,0.2)", borderRadius:12, padding:"1rem", textAlign:"center", marginTop:"1rem" }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"2rem", fontWeight:800, color:"#38bdf8" }}>₹{fmt(perPerson)}</div>
              <small style={{ color:"var(--muted)", fontSize:".8rem" }}>Per Person · {days} days · {to}</small>
            </div>
          </div>
        </div>
      </Card>
      <div style={{ background:"linear-gradient(135deg,rgba(74,222,128,0.1),rgba(56,189,248,0.1))", border:"1px solid rgba(74,222,128,0.25)", borderRadius:12, padding:"1rem 1.5rem", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"1rem", marginTop:"1.5rem" }}>
        <div>
          <strong style={{ display:"block", fontSize:".95rem" }}>📄 Share or Save Your Plan</strong>
          <p style={{ color:"var(--muted)", fontSize:".85rem" }}>Copy your trip summary or share via WhatsApp</p>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <button onClick={shareWA}  style={{ padding:"8px 16px", borderRadius:10, fontSize:".78rem", fontWeight:600, cursor:"pointer", border:"none", background:"#25D366", color:"#fff" }}>📱 WhatsApp</button>
          <button onClick={copyPlan} style={{ padding:"8px 16px", borderRadius:10, fontSize:".78rem", fontWeight:600, cursor:"pointer", border:"1px solid var(--border)", background:"var(--card2)", color:"var(--text)" }}>📋 Copy Plan</button>
        </div>
      </div>
      <BtnRow>
        <Btn variant="secondary" onClick={onBack}>← Back</Btn>
        <Btn onClick={onReset}>🔁 Plan Another Trip</Btn>
      </BtnRow>
    </div>
  );
}

// ─── STEPS CONFIG ─────────────────────────────────────────────────────────────
const STEPS = [
  { n:1, label:"Trip Details"  },
  { n:2, label:"Transport"     },
  { n:3, label:"Route Map"     },
  { n:4, label:"Hotels"        },
  { n:5, label:"Restaurants"   },
  { n:6, label:"Places"        },
  { n:7, label:"Itinerary"     },
  { n:8, label:"Budget"        },
];

const INIT_TRIP = {
  from:"", to:"", date:today, returnDate:threeDaysLater, travellers:2, budgetType:"mid", days:3,
  transport:null, hotel:null, restaurants:[], places:[],
  transportData:[], hotelData:[], restaurantData:[], placesData:[],
};

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function TripPlanner() {
  const [step,    setStep]    = useState(1);
  const [trip,    setTrip]    = useState(INIT_TRIP);
  const [loading, setLoading] = useState({ transport:false, hotels:false, restaurants:false, places:false });

  const setLoad = (k, v) => setLoading(l => ({ ...l, [k]: v }));
  const updTrip = (patch)  => setTrip(t => ({ ...t, ...patch }));
  const goStep  = (n)      => { setStep(n); window.scrollTo({ top:0, behavior:"smooth" }); };

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = GOOGLE_FONTS + CSS;
    document.head.appendChild(style);
    if (!document.querySelector("[data-leaflet-css]")) {
      const lc = document.createElement("link");
      lc.rel = "stylesheet"; lc.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      lc.dataset.leafletCss = "1"; document.head.appendChild(lc);
    }
    if (!document.querySelector("[data-leaflet-js]")) {
      const ls = document.createElement("script");
      ls.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      ls.dataset.leafletJs = "1"; document.body.appendChild(ls);
    }
  }, []);

  // ── Detect if a city is in India ─────────────────────────────────────────────
  const isIndianCity = (city) => {
    const india = ["mumbai","delhi","chennai","kolkata","bangalore","bengaluru","hyderabad","pune","ahmedabad","jaipur","surat","lucknow","kanpur","nagpur","indore","bhopal","patna","vadodara","goa","kochi","coimbatore","madurai","visakhapatnam","agra","varanasi","manali","shimla","dharamsala","ooty","mysuru","mysore","karur","salem","trichy","tiruchirappalli","vellore","pondicherry","tirunelveli","erode","dindigul","thanjavur","kanchipuram","amritsar","chandigarh","ludhiana","dehradun","haridwar","rishikesh","jammu","srinagar","bhubaneswar","cuttack","ranchi","guwahati","shillong","imphal","aizawl","gangtok","kohima","itanagar","dispur","raipur","bhopal","jabalpur","gwalior","ujjain","aurangabad","nashik","kolhapur","solapur","rajkot","bhavnagar","jamnagar","jodhpur","udaipur","kota","bikaner","ajmer","allahabad","prayagraj","meerut","ghaziabad","noida","faridabad","gurugram","gurgaon"];
    return india.some(c => city.toLowerCase().includes(c));
  };

  const handleSearch = useCallback(async (form) => {
    const d1   = new Date(form.date);
    const d2   = new Date(form.returnDate);
    const days = isNaN(d1) || isNaN(d2) ? 3 : Math.max(1, Math.round((d2 - d1) / 86400000));
    updTrip({ ...form, days, transport:null, hotel:null, restaurants:[], places:[], transportData:[], hotelData:[], restaurantData:[], placesData:[] });
    goStep(2);

    const domestic = isIndianCity(form.from) && isIndianCity(form.to);
    const sym      = domestic ? "INR" : "USD";

    // ── Transport ──────────────────────────────────────────────────────────────
    setLoad("transport", true);
    try {
      const transportPrompt = [
        "You are a travel pricing API. Return ONLY a JSON object, no explanation.",
        "Route: " + form.from + " to " + form.to + ". Budget type: " + form.budgetType + ".",
        domestic
          ? "Both cities are in India. Use INR currency. currency_symbol is INR symbol."
          : "This is an international route. Use USD currency. currency_symbol is dollar sign. Add inr_equivalent field = price multiplied by 92.",
        "Give exactly 3 options: Flight, Train, Bus (if train/bus impractical for international, use Ferry or Taxi instead).",
        "CRITICAL - Realistic duration rules:",
        "- Flight cruises at 850 km/h. Calculate flight time from real distance.",
        "- Train averages 70 km/h. Chennai to Kolkata is 1400km so 20 hours minimum.",
        "- Bus averages 50 km/h. Chennai to Kolkata is 1400km so 28 hours minimum.",
        "- Long journeys over 12 hours: departure must be evening 19:00-22:00, arrival next day or day after.",
        domestic
          ? "India price rules per person: Flight under 800km=2500to4500, 800to1500km=4500to8000, over 1500km=7000to12000 INR. Train AC2T = distance km times 2.5 INR. Bus AC Sleeper = distance km times 0.8 INR."
          : "International price rules per person in USD: under 3h flight=120to350, 3to8h=300to800, over 8h=600to1800.",
        "Use REAL provider names: actual airlines, real Indian train names with numbers, real bus operators.",
        "Respond with ONLY this JSON structure:",
        JSON.stringify({
          options: [
            { type:"Flight", provider:"IndiGo 6E-204", price: domestic?6500:350, currency_symbol: domestic?"₹":"$", inr_equivalent: domestic?6500:32200, duration:"1h 50m", departure:"06:00", arrival:"07:50", comfort:"High", features:["Snack","15kg Baggage"] },
            { type:"Train",  provider:"Mail Express 12345", price: domestic?3200:180, currency_symbol: domestic?"₹":"$", inr_equivalent: domestic?3200:16560, duration:"22h", departure:"21:00", arrival:"19:00+1", comfort:"Medium", features:["AC 2T","Pantry Car"] },
            { type:"Bus",    provider:"TNSTC Volvo AC", price: domestic?900:60, currency_symbol: domestic?"₹":"$", inr_equivalent: domestic?900:5520, duration:"28h", departure:"20:00", arrival:"00:00+2", comfort:"Low", features:["AC Sleeper","USB Charging"] }
          ]
        })
      ].join(" ");
      const d = await callAI(transportPrompt);
      updTrip({ transportData: Array.isArray(d.options) ? d.options : fallbackTransport() });
    } catch(e) { console.error("transport err", e); updTrip({ transportData: fallbackTransport() }); }
    setLoad("transport", false);

    // ── Hotels ─────────────────────────────────────────────────────────────────
    setLoad("hotels", true);
    try {
      const hotelPrompt = [
        "You are a hotel data API. Return ONLY a JSON object, no explanation.",
        "List 4 real hotels in " + form.to + " for " + form.budgetType + " budget travelers.",
        domestic
          ? "City is in India. Use INR. Price per night: budget=500to1500, mid=1500to5000, luxury=5000to25000."
          : "International city. Use USD. Price per night: budget=30to80, mid=80to200, luxury=200to800. Add inr_equivalent = price times 92.",
        "Use REAL hotel names that actually exist in " + form.to + ". No made-up names.",
        "Respond with ONLY this JSON:",
        JSON.stringify({
          hotels: [
            { name:"Real Hotel Name", area:"Actual Locality", stars:4, rating:4.5, reviews:1200, price_per_night:2500, currency_symbol: domestic?"₹":"$", inr_equivalent:2500, amenities:["Pool","WiFi","Restaurant"], tip:"Book 2 weeks in advance" }
          ]
        })
      ].join(" ");
      const d = await callAI(hotelPrompt);
      updTrip({ hotelData: Array.isArray(d.hotels) ? d.hotels : fallbackHotels() });
    } catch(e) { console.error("hotels err", e); updTrip({ hotelData: fallbackHotels() }); }
    setLoad("hotels", false);

    // ── Restaurants ────────────────────────────────────────────────────────────
    setLoad("restaurants", true);
    try {
      const restPrompt = [
        "You are a restaurant data API. Return ONLY a JSON object, no explanation.",
        "List 6 real top-rated restaurants in " + form.to + " for " + form.budgetType + " budget travelers.",
        domestic
          ? "City is in India. Use INR. Avg meal per person: budget=100to300, mid=300to800, luxury=800to3000."
          : "International city. Use USD. Avg meal per person: budget=5to15, mid=15to50, luxury=50to200. Add inr_equivalent = price times 92.",
        "Use REAL restaurant names in " + form.to + ". Include local and popular cuisines of that city.",
        "Respond with ONLY this JSON:",
        JSON.stringify({
          restaurants: [
            { name:"Real Restaurant", area:"Locality", cuisine:"Local Cuisine", rating:4.5, reviews:800, avg_meal_per_person:350, currency_symbol: domestic?"₹":"$", inr_equivalent:350, specialty:"Signature Dish", open_hours:"11am-10pm", tags:["Must Try","Local Fav"] }
          ]
        })
      ].join(" ");
      const d = await callAI(restPrompt);
      updTrip({ restaurantData: Array.isArray(d.restaurants) ? d.restaurants : fallbackRestaurants() });
    } catch(e) { console.error("restaurants err", e); updTrip({ restaurantData: fallbackRestaurants() }); }
    setLoad("restaurants", false);

    // ── Places ─────────────────────────────────────────────────────────────────
    setLoad("places", true);
    try {
      const placesPrompt = [
        "You are a tourist attractions API. Return ONLY a JSON object, no explanation.",
        "List 6 real must-visit tourist attractions in " + form.to + ".",
        domestic
          ? "City is in India. Use INR for entry fees. Free=0, budget=10to100, monuments=100to1000."
          : "International city. Use USD for entry fees. Free=0, typical=5to50. Use real entry prices for famous landmarks. Add inr_equivalent = price times 92.",
        "Use REAL attraction names that exist in " + form.to + ". Include monuments, nature, museums, markets.",
        "Respond with ONLY this JSON:",
        JSON.stringify({
          places: [
            { name:"Real Attraction", area:"District", entry_fee:50, currency_symbol: domestic?"₹":"$", inr_equivalent:50, duration_hrs:2, category:"Monument", rating:4.6, reviews:5000, tip:"Visit early morning" }
          ]
        })
      ].join(" ");
      const d = await callAI(placesPrompt);
      updTrip({ placesData: Array.isArray(d.places) ? d.places : fallbackPlaces() });
    } catch(e) { console.error("places err", e); updTrip({ placesData: fallbackPlaces() }); }
    setLoad("places", false);
  }, []);

  const toggleRestaurant = (r) => updTrip({ restaurants: trip.restaurants.some(x=>x.name===r.name) ? trip.restaurants.filter(x=>x.name!==r.name) : [...trip.restaurants, r] });
  const togglePlace      = (p) => updTrip({ places:      trip.places.some(x=>x.name===p.name)      ? trip.places.filter(x=>x.name!==p.name)           : [...trip.places, p]      });
  const reset            = ()  => { updTrip(INIT_TRIP); goStep(1); };

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg)", color:"var(--text)", fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ position:"fixed", inset:0, background:"radial-gradient(ellipse at 20% 20%,rgba(249,115,22,.08) 0%,transparent 50%),radial-gradient(ellipse at 80% 80%,rgba(56,189,248,.06) 0%,transparent 50%)", pointerEvents:"none", zIndex:0 }} />

      <header style={{ position:"sticky", top:0, zIndex:100, background:"rgba(10,14,26,0.92)", backdropFilter:"blur(20px)", borderBottom:"1px solid var(--border)", padding:"0 2rem", display:"flex", alignItems:"center", justifyContent:"space-between", height:64 }}>
        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:"1.4rem", letterSpacing:"-.5px" }}>
          Trip<span style={{ color:"#f97316" }}>Planner</span>
          <small style={{ fontSize:".6rem", fontWeight:400, color:"var(--muted)", marginLeft:6, letterSpacing:2, textTransform:"uppercase", verticalAlign:"middle" }}>Pro</small>
        </div>
        <span style={{ background:"rgba(74,222,128,0.15)", color:"#4ade80", border:"1px solid rgba(74,222,128,0.3)", padding:"4px 12px", borderRadius:20, fontSize:".72rem", fontWeight:500, letterSpacing:1 }}>✦ Free Forever</span>
      </header>

      <div style={{ position:"relative", zIndex:1, textAlign:"center", padding:"60px 2rem 40px" }}>
        <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:"clamp(2rem,5vw,3.5rem)", fontWeight:800, lineHeight:1.1, letterSpacing:-1, marginBottom:"1rem" }}>
          Plan Your{" "}
          <span style={{ background:"linear-gradient(135deg,#f97316,#fb923c,#fbbf24)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>Perfect Trip</span>
          <br />from A to Z
        </h1>
        <p style={{ color:"var(--muted)", fontSize:"1.05rem", maxWidth:540, margin:"0 auto" }}>
          Transport · Hotels · Restaurants · Places · Budget — all in one place, powered by AI
        </p>
      </div>

      <div style={{ display:"flex", overflowX:"auto", borderBottom:"1px solid var(--border)", padding:"0 2rem", position:"relative", zIndex:1, scrollbarWidth:"none" }}>
        {STEPS.map(s => {
          const active = step === s.n;
          const done   = step > s.n;
          return (
            <button key={s.n} onClick={() => goStep(s.n)} style={{ padding:"14px 20px", fontSize:".8rem", fontWeight:500, color: active?"#f97316":done?"var(--text)":"var(--muted)", borderBottom:`2px solid ${active?"#f97316":"transparent"}`, borderTop:"none", borderLeft:"none", borderRight:"none", whiteSpace:"nowrap", cursor:"pointer", display:"flex", alignItems:"center", gap:8, background:"none", transition:"all .2s" }}>
              <span style={{ width:22, height:22, borderRadius:"50%", background:active?"#f97316":done?"#4ade80":"var(--border)", color:active?"#fff":done?"#000":"var(--muted)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:".7rem", fontWeight:700, flexShrink:0 }}>
                {done ? "✓" : s.n}
              </span>
              {s.label}
            </button>
          );
        })}
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"2rem 1.5rem", position:"relative", zIndex:1 }}>
        {step === 1 && <StepDetails onSearch={handleSearch} />}
        {step === 2 && <StepTransport data={trip.transportData} loading={loading.transport} selected={trip.transport} onSelect={t=>updTrip({transport:t})} onNext={()=>goStep(3)} onBack={()=>goStep(1)} />}
        {step === 3 && <StepMap from={trip.from} to={trip.to} onNext={()=>goStep(4)} onBack={()=>goStep(2)} />}
        {step === 4 && <StepHotels data={trip.hotelData} loading={loading.hotels} selected={trip.hotel} onSelect={h=>updTrip({hotel:h})} onNext={()=>goStep(5)} onBack={()=>goStep(3)} />}
        {step === 5 && <StepRestaurants data={trip.restaurantData} loading={loading.restaurants} selected={trip.restaurants} onToggle={toggleRestaurant} onNext={()=>goStep(6)} onBack={()=>goStep(4)} />}
        {step === 6 && <StepPlaces data={trip.placesData} loading={loading.places} selected={trip.places} onToggle={togglePlace} onNext={()=>goStep(7)} onBack={()=>goStep(5)} />}
        {step === 7 && <StepItinerary tripState={trip} onNext={()=>goStep(8)} onBack={()=>goStep(6)} />}
        {step === 8 && <StepBudget tripState={trip} onBack={()=>goStep(7)} onReset={reset} />}
      </div>
    </div>
  );
}
